import { excludeMatchiPayments } from "@/lib/matchiRevenue";

/** Export an array of objects as a CSV file download (UTF-8 with BOM for Excel). */
export function exportToCsv(filename: string, rows: Record<string, unknown>[]) {
  if (rows.length === 0) return;
  const headers = Object.keys(rows[0]);
  const csvRows = [
    headers.join(";"),
    ...rows.map((r) =>
      headers
        .map((h) => {
          const v = r[h];
          const s = v == null ? "" : String(v);
          return `"${s.replace(/"/g, '""')}"`;
        })
        .join(";")
    ),
  ];
  const bom = "\uFEFF";
  const blob = new Blob([bom + csvRows.join("\n")], { type: "text/csv;charset=utf-8;" });
  downloadBlob(blob, filename);
}

/** Export as formatted Excel (.xlsx) */
export async function exportToExcel(filename: string, rows: Record<string, unknown>[], sheetName = "Data") {
  if (rows.length === 0) return;
  const ExcelJS = await import("exceljs");
  const wb = new ExcelJS.default.Workbook();
  const ws = wb.addWorksheet(sheetName);

  const headers = Object.keys(rows[0]);

  // Auto column widths
  ws.columns = headers.map((h) => {
    const maxLen = Math.max(h.length, ...rows.map((r) => String(r[h] ?? "").length));
    return { header: h, key: h, width: Math.min(maxLen + 2, 40) };
  });

  // Add data rows (headers already set via ws.columns)
  for (const row of rows) {
    ws.addRow(headers.map((h) => row[h] ?? ""));
  }

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  downloadBlob(blob, filename);
}

/**
 * Build a multi-sheet Excel workbook from named data arrays.
 * Used by LeagueCSVExport for combined export.
 */
export async function exportMultiSheetExcel(
  filename: string,
  sheets: { name: string; rows: Record<string, unknown>[] }[]
) {
  const ExcelJS = await import("exceljs");
  const wb = new ExcelJS.default.Workbook();

  for (const sheet of sheets) {
    if (sheet.rows.length === 0) continue;
    const ws = wb.addWorksheet(sheet.name);
    const headers = Object.keys(sheet.rows[0]);
    ws.columns = headers.map((h) => ({ header: h, key: h, width: 20 }));
    for (const row of sheet.rows) {
      ws.addRow(headers.map((h) => row[h] ?? ""));
    }
  }

  const buf = await wb.xlsx.writeBuffer();
  const blob = new Blob([buf], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
  downloadBlob(blob, filename);
}

/**
 * Sprint D — Sie-payment shape with dual-state input support.
 *
 * During the 2-week cutover window, prod data has both:
 *   - `amount` (legacy) — kronor for AP Nacka rows, öre for Demo rows (mixed-unit pre-migration)
 *   - `amount_minor` (new) — always öre, populated by Phase 3 backfill
 *
 * The writer prefers `amount_minor` when present (correct, unit-clean) and
 * falls back to `amount` only when `amount_minor` is NULL — which can only
 * happen for rows inserted between Phase 3 backfill and code-cutover deploy.
 *
 * Currency support: `currency` from the row (default 'SEK') is recorded in the
 * SIE comment but does NOT affect amount-line emission (SIE is single-currency
 * per file by spec; cross-currency ledgers split into multiple files).
 */
export type SiePaymentInput = {
  id: string;
  amount?: number | null;          // legacy kronor (or öre for demo) — fallback only
  amount_minor?: number | null;    // preferred — always öre
  currency?: string | null;
  payment_method: string | null;
  payment_method_id?: string | null; // C3: MATCHi-fk — see isMatchiSettledPayment
  payment_type?: string | null;
  paid_at: string | null;
  created_at: string;
};

/**
 * Resolve a payment row to **decimal kronor** for SIE emission.
 *
 * Logic:
 *   1. If `amount_minor` is a finite number → return `amount_minor / 100`
 *   2. Else if `amount` is a finite number → return `amount` (legacy: assumed kronor)
 *   3. Else → throw (SIE forbids null-amount rows)
 *
 * Exported for testability + reuse by other accounting writers (if any).
 */
export function resolveSieAmountKronor(p: Pick<SiePaymentInput, "amount" | "amount_minor" | "id">): number {
  if (typeof p.amount_minor === "number" && Number.isFinite(p.amount_minor)) {
    return p.amount_minor / 100;
  }
  if (typeof p.amount === "number" && Number.isFinite(p.amount)) {
    return p.amount;
  }
  throw new Error(`exportToSie: payment ${p.id} has neither amount_minor nor amount`);
}

/**
 * Build the SIE Type 4 text body for a payment list.
 *
 * Pure function — no I/O. Exported for unit tests + reuse.
 *
 * Sprint D: writer-boundary unit conversion via `resolveSieAmountKronor`.
 * Output is decimal kronor with `.` decimal separator (SIE spec, locale-independent)
 * and 2 decimals (BAS chart-of-accounts norm).
 */
export function buildSieText(payments: SiePaymentInput[], now: Date = new Date()): string {
  // C3/BES-004: MATCHi-avräknade betalningar hör inte hemma i Boplys bokföring.
  // Pengarna togs emot av MATCHi och redovisas i deras utbetalningsunderlag — att
  // emittera dem här skapade falska verifikationer (1930 debet påstår att pengarna
  // ligger på Boplys Stripe-konto; 3010 kredit dubbelräknar intäkten). Filtret sitter
  // i kärnan, inte hos anroparen, så varje nuvarande och framtida exportväg är täckt.
  // Semantik: SIE EXKLUDERAR; rapportytorna SÄRREDOVISAR (splitMatchiPayments).
  const sieRows = excludeMatchiPayments(payments);

  const yearStart = `${now.getFullYear()}0101`;
  const yearEnd = `${now.getFullYear()}1231`;

  // BAS account mapping
  const REVENUE_ACCOUNTS: Record<string, string> = {
    court_booking: "3010",  // Intäkter bana (6% moms)
    booking: "3010",
    tournament: "3011",     // Intäkter turneringar
    training: "3012",       // Intäkter träning
    membership: "3013",     // Intäkter medlemskap
    punchcard: "3014",      // Intäkter klippkort
    credits: "3014",
    addons: "3015",         // Intäkter tillägg (25% moms)
  };

  const METHOD_ACCOUNTS: Record<string, string> = {
    card: "1930",       // Stripe/kortkonto
    stripe: "1930",
    swish: "1580",      // Swish-konto
    manual: "1910",     // Kassa
    cash: "1910",
    invoice: "1510",    // Kundfordringar
    free: "3999",       // Gratisbokningar
  };

  // Header
  const lines: string[] = [
    '#FLAGGA 0',
    '#PROGRAM "Boply" "1.0"',
    '#FORMAT PC8',
    '#GEN ' + format8(now),
    '#SIETYP 4',
    `#RAR 0 ${yearStart} ${yearEnd}`,
    `#FNAMN "Boply Export"`,
    '',
  ];

  // Verifications — Boply-betalda rader endast (se sieRows ovan).
  sieRows.forEach((p, i) => {
    const date = p.paid_at ? format8(new Date(p.paid_at)) : format8(new Date(p.created_at));
    const type = categorizeForSie(p.payment_type);
    const revenueAccount = REVENUE_ACCOUNTS[type] || "3010";
    const methodAccount = METHOD_ACCOUNTS[p.payment_method || "card"] || "1930";

    // Sprint D: writer-boundary conversion via minor-aware resolver.
    // Always emits decimal kronor with 2 decimals (BAS spec).
    const amount = resolveSieAmountKronor(p);

    lines.push(`#VER "" "${i + 1}" ${date} "Betalning ${p.id.slice(0, 8)}"`);
    lines.push('{');
    lines.push(`\t#TRANS ${methodAccount} {} ${amount.toFixed(2)}`);
    lines.push(`\t#TRANS ${revenueAccount} {} ${(-amount).toFixed(2)}`);
    lines.push('}');
  });

  return lines.join('\r\n');
}

/**
 * Export payments as a SIE Type 4 file for Swedish accounting systems (Fortnox, Visma, etc.)
 * SIE = Standard Import Export for accounting data.
 *
 * Sprint D: writer-boundary unit conversion. Prefers `amount_minor` when
 * available; falls back to legacy `amount`. Output is decimal kronor with
 * `.` decimal separator (SIE spec) and 2 decimals (BAS chart-of-accounts norm).
 */
export function exportToSie(
  filename: string,
  payments: SiePaymentInput[]
) {
  if (payments.length === 0) return;

  const text = buildSieText(payments);

  // SIE files use ISO-8859-1 encoding (PC8)
  // Browser TextEncoder only supports UTF-8, so we do a best-effort Latin-1 conversion
  const bytes = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i++) {
    bytes[i] = text.charCodeAt(i) & 0xFF;
  }
  const blob = new Blob([bytes], { type: "application/octet-stream" });
  downloadBlob(blob, filename);
}

function format8(d: Date): string {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}${m}${day}`;
}

function categorizeForSie(paymentType: string | null | undefined): string {
  const t = (paymentType || "").toLowerCase();
  if (t.includes("tournament")) return "tournament";
  if (t.includes("training") || t.includes("lesson")) return "training";
  if (t.includes("member")) return "membership";
  if (t.includes("punch")) return "punchcard";
  if (t.includes("credit")) return "credits";
  if (t.includes("addon")) return "addons";
  return "booking";
}

/** Trigger the browser print dialog for PDF export. */
export function exportToPdf() {
  window.print();
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
